import {signal} from '@angular/core';

import {LaunchDarklyService} from '../launch_darkly_service';
import {LaunchDarklyKey, RuntimeConfig} from '../launch_darkly_types';

type ApplicationName = 'gpt-t' | 'gpt-d' | 'grid-ops' | 'grid-ready' | 'hyper-q' | 'model-builder';

/**
 * Feedback And Analytics Resolver
 *
 * A single-source resolution mechanism for analytics
 * and feedback. We used to source these variables
 * from combination of command line arguments, protos,
 * and potentially network requests - in addition to having
 * them exposed potentially in plain text.
 *
 * This resolver moves their resolution to launch darkly
 * where the appropriate value per-environment will be
 * chosen at runtime for you.
 *
 * cc: app.launchdarkly.com
 */
export class FeedbackAndAnalyticsResolver {
  public trackingID = '';
  public feedbackBucket = '';
  public feedbackProductID = '';
  public enableAnonymousFeedback = true;
  public disableScreenshotting = false;
  public analyticsEnabledForDeployment = false;
  public readonly analyticsEnabledForCurrentUser = signal(false);

  constructor(private readonly appName: ApplicationName) {}

  initialize() {
    const runtimeConfig = LaunchDarklyService.evaluate<RuntimeConfig>(
      LaunchDarklyKey.RUNTIME_CONFIG,
      {},
    );
    const appConfig = runtimeConfig.applications?.[this.appName];

    if (appConfig) {
      this.trackingID = appConfig.analytics?.trackingId || '';
      this.analyticsEnabledForDeployment = appConfig.analytics?.enabled || false;
      this.feedbackBucket = appConfig.feedback?.bucketId || '';
      this.feedbackProductID = appConfig.feedback?.productId || '';
      this.enableAnonymousFeedback = appConfig.feedback?.enableAnonymousFeedback ?? true;
      this.disableScreenshotting = appConfig.feedback?.disableScreenshotting ?? false;
    }

    this.analyticsEnabledForCurrentUser.set(this.validAnalyticsConfiguration);
  }

  /**
   * Disable Tracking if Internal
   *
   * Certain apps look to disable google analytics when the current
   * user is using a google account. This method will do that for you.
   * You can call it once and forget about it.
   */
  public disableTrackingIfInternal(isInternalUser: boolean): boolean {
    if (this.trackingID) {
      const disabled = isInternalUser || !this.validAnalyticsConfiguration;
      // `configurable`/`writable` matter here: more than one resolver can run per page
      // load (e.g. the root-injector GPT-T service is always constructed at bootstrap).
      // If two of them share a tracking ID and disagree on `disabled`, a locked property
      // would throw `Cannot redefine property` from inside a promise callback.
      Object.defineProperty(window, `ga-disable-${this.trackingID}`, {
        value: disabled,
        configurable: true,
        writable: true,
      });
      this.analyticsEnabledForCurrentUser.set(!disabled);
      return disabled;
    }
    return true;
  }

  public get isFeedbackEnabledForCurrentUser() {
    return !!this.feedbackBucket && !!this.feedbackProductID;
  }

  private get validAnalyticsConfiguration() {
    return !!this.trackingID && !!this.analyticsEnabledForDeployment;
  }
}
